import { useCallback, useMemo } from "react";

import {
  buildCurrencyOptions,
  CurrencyRates,
  formatCurrency,
  normalizeCurrencyCode,
  SupportedCurrency,
} from "helpers/currency";
import { useStoreActions, useStoreState } from "store/store";

const TWELVE_HOURS_IN_MS = 12 * 60 * 60 * 1000;
const QUOTE_REFRESH_BUFFER_MS = 5 * 60 * 1000;

const hasRateForCurrency = (
  rates: CurrencyRates | undefined,
  currency: SupportedCurrency
): boolean => {
  if (!rates) return false;
  const rate = rates[currency];
  return Number.isFinite(rate);
};

export const useCurrency = () => {
  const selectedCurrency = useStoreState((state) => state.currency);
  const currencyRates = useStoreState((state) => state.currencyRates);
  const currencyRatesFetchedAt = useStoreState(
    (state) => state.currencyRatesFetchedAt
  );
  const currencyRatesExpiresAt = useStoreState(
    (state) => state.currencyRatesExpiresAt
  );
  const currencyRatesLoading = useStoreState(
    (state) => state.currencyRatesLoading
  );
  const supportedCurrencies = useStoreState(
    (state) => state.supportedCurrencies
  );
  const currencyMetadata = useStoreState((state) => state.currencyMetadata);

  const setCurrencyAction = useStoreActions((actions) => actions.setCurrency);
  const fetchCurrencyRates = useStoreActions(
    (actions) => actions.fetchCurrencyRatesThunk
  );
  const updateCart = useStoreActions((actions) => actions.updateCartThunk);

  const normalizedCurrency = normalizeCurrencyCode(selectedCurrency);

  const shouldRefreshRates = useCallback(() => {
    if (!hasRateForCurrency(currencyRates, normalizedCurrency)) {
      return true;
    }

    if (!currencyRatesFetchedAt) {
      return true;
    }

    const now = Date.now();

    if (
      currencyRatesExpiresAt &&
      currencyRatesExpiresAt - QUOTE_REFRESH_BUFFER_MS <= now
    ) {
      return true;
    }

    return now - currencyRatesFetchedAt > TWELVE_HOURS_IN_MS;
  }, [
    currencyRates,
    currencyRatesExpiresAt,
    currencyRatesFetchedAt,
    normalizedCurrency,
  ]);

  const ensureRatesLoaded = useCallback(() => {
    if (currencyRatesLoading) {
      return;
    }

    if (shouldRefreshRates()) {
      fetchCurrencyRates();
    }
  }, [currencyRatesLoading, fetchCurrencyRates, shouldRefreshRates]);

  const formatPrice = useCallback(
    (
      amount: number | string | null | undefined,
      options?: Intl.NumberFormatOptions
    ) => formatCurrency(amount, normalizedCurrency, options, currencyMetadata),
    [normalizedCurrency, currencyMetadata]
  );

  const setCurrency = useCallback(
    (value: SupportedCurrency | string) => {
      const normalizedValue = normalizeCurrencyCode(value);

      if (normalizedValue === normalizedCurrency) {
        return;
      }

      setCurrencyAction(normalizedValue);

      if (!hasRateForCurrency(currencyRates, normalizedValue)) {
        fetchCurrencyRates();
      }

      void updateCart({}).catch(() => undefined);
    },
    [
      setCurrencyAction,
      currencyRates,
      fetchCurrencyRates,
      normalizedCurrency,
      updateCart,
    ]
  );

  const currencyOptions = useMemo(() => {
    const list = supportedCurrencies.length
      ? supportedCurrencies
      : [normalizedCurrency];

    return buildCurrencyOptions(list, currencyMetadata);
  }, [supportedCurrencies, normalizedCurrency, currencyMetadata]);

  return {
    currency: normalizedCurrency,
    setCurrency,
    formatPrice,
    currencyOptions,
    rates: currencyRates,
    ensureRatesLoaded,
    supportedCurrencies,
    currencyMetadata,
  };
};
